import classNames from "classnames";
import { useDelayedEvent } from "client/Hooks";
import { ensureNotNil, setTrackingId } from "client/Misc/utils";
import {
	createContext,
	MouseEvent,
	PropsWithChildren,
	ReactElement,
	Ref,
	RefObject,
	useContext,
	useMemo,
	useRef,
	useState
} from "react";
import styles from "./ElementLayout.module.less";
import { InlineActionList } from "../Actions/InlineActionList";
import { ButtonBase, Icon, IconButton } from "client/Components";
import { AddElementMenu } from "../Layout/Menus/AddElementMenu";
import { HubItemElementFormModel } from "../../Management/common";
import { EmbedInputDialogData } from "../../HubMediaSelect/EmbedInputDialog";
import { VideoInputDialogData } from "../../HubMediaSelect/VideoInputDialog";
import { HubImageSelectorKind, ImageWithAttribution } from "../../Management/useHubImageSelector";
import { localize } from "client_legacy/ndaw/culture";
import { useAiAssistantBranding } from "client/Features/ChatAssistant/useAiAssistantBranding";
import { AiAssistantAvatar } from "client/Features/ChatAssistant/AiAssistantAvatar";

export type ElementLayoutProps = {
	trackingId: string;
	hasError: boolean;
	onCloseAllMenus?: () => void;
	ref?: Ref<HTMLDivElement>;
};

export const ElementLayout = (props: PropsWithChildren<ElementLayoutProps>): ReactElement => {
	const {
		trackingId,
		hasError,
		onCloseAllMenus,
		ref,
		children
	} = props;

	const [isFocused, setIsFocused] = useState<boolean>(false);

	// We need a delay because clicking an
	// item inside the add element menu fires
	// blur before focusing the element
	const [blurHandler, cancelBlurHandler] = useDelayedEvent(
		(): void => {
			setIsFocused(false);
			onCloseAllMenus?.();
		},
		0
	);

	const contextData = useMemo(
		(): ElementLayoutContextData => ({
			isFocused
		}),
		[isFocused]
	);

	return (
		<div
			{...setTrackingId(trackingId)}
			className={classNames(
				styles.root,
				{
					[styles.root_active]: isFocused,
					[styles.root_hasError]: hasError
				}
			)}
			tabIndex={1}
			onClick={(e)=> {
				e.stopPropagation();
			}}
			onFocus={(e)=> {
				e.stopPropagation();
				cancelBlurHandler();
				setIsFocused(true);
			}}
			onBlur={blurHandler}
			ref={ref}
		>
			<ElementLayoutContext.Provider value={contextData}>
				{children}
			</ElementLayoutContext.Provider>
		</div>
	);
};
ElementLayout.displayName = "ElementLayout";

export type ActionsMenuProps = {
	trackingId: string;
	visibilityPreference?: "when-container-is-focused" | "always";
};

const ActionsMenu = (props: PropsWithChildren<ActionsMenuProps>): ReactElement | null => {
	const {
		trackingId,
		visibilityPreference = "when-container-is-focused",
		children
	} = props;

	const { isFocused } = useContext(ElementLayoutContext);

	if (!isFocused && visibilityPreference === "when-container-is-focused") {
		return null;
	}

	return (
		<div className={styles.actionsDropOverAnchor}>
			<div className={styles.actionsDropOver}>
				<InlineActionList trackingId={trackingId}>
					{children}
				</InlineActionList>
			</div>
		</div>
	);
};
ActionsMenu.displayName = "ElementLayout.ActionsMenu";

type AddElementMenuItemProps = {
	trackingId: string;
	onAddNewElementToColumn: (element: HubItemElementFormModel) => void;
	onOpenEmbedInputDialog: (data: EmbedInputDialogData) => Promise<TryResult<string>>;
	onOpenVideoInputDialog: (data: VideoInputDialogData) => Promise<TryResult<string>>;
	onSelectImage: (kind: HubImageSelectorKind) => Promise<TryResult<ImageWithAttribution>>;
};

const AddElementMenuItem = (props: AddElementMenuItemProps): ReactElement => {
	const {
		trackingId,
		onAddNewElementToColumn,
		onOpenEmbedInputDialog,
		onOpenVideoInputDialog,
		onSelectImage
	} = props;

	const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);

	const ref: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

	return (
		<>
			<IconButton
				ref={ref}
				kind="transparent-colored"
				icon="plus"
				onClick={() => setIsMenuOpen(true)}
				trackingId="add-button"
			/>
			{isMenuOpen && (
				<AddElementMenu
					target={ensureNotNil(ref.current)}
					onClose={() => setIsMenuOpen(false)}
					onNewElement={onAddNewElementToColumn}
					onOpenEmbedInputDialog={onOpenEmbedInputDialog}
					onOpenVideoInputDialog={onOpenVideoInputDialog}
					onSelectImage={onSelectImage}
					trackingId={trackingId}
				/>
			)}
		</>
	);
};
AddElementMenuItem.displayName = "ElementLayout.AddElementMenuItem";

type MoveUpMenuItemProps = {
	onClick: () => void;
};

const MoveUpMenuItem = (props: MoveUpMenuItemProps): ReactElement => {
	const {
		onClick
	} = props;

	return (
		<IconButton
			kind="transparent-colored"
			icon="arrow-up"
			onClick={onClick}
			trackingId="move-up-button"
		/>
	);
};
MoveUpMenuItem.displayName = "ElementLayout.MoveUpMenuItem";

type MoveDownMenuItemProps = {
	onClick: () => void;
};

const MoveDownMenuItem = (props: MoveDownMenuItemProps): ReactElement => {
	const {
		onClick
	} = props;

	return (
		<IconButton
			kind="transparent-colored"
			icon="arrow-down"
			onClick={onClick}
			trackingId="move-down-button"
		/>
	);
};
MoveDownMenuItem.displayName = "ElementLayout.MoveDownMenuItem";

type EditMenuItemProps = {
	onClick: () => void;
	ref?: Ref<HTMLButtonElement>;
};

const EditMenuItem = (props: EditMenuItemProps): ReactElement => {
	const {
		onClick,
		ref
	} = props;

	return (
		<IconButton
			ref={ref}
			kind="transparent-colored"
			icon="pen"
			onClick={onClick}
			trackingId="edit-button"
		/>
	);
};
EditMenuItem.displayName = "ElementLayout.EditMenuItem";

type DuplicateMenuItemProps = {
	onClick: () => void;
};

const DuplicateMenuItem = (props: DuplicateMenuItemProps): ReactElement => {
	const {
		onClick
	} = props;

	return (
		<IconButton
			kind="transparent-colored"
			icon="copy"
			onClick={onClick}
			trackingId="duplicate-button"
		/>
	);
};
DuplicateMenuItem.displayName = "ElementLayout.DuplicateMenuItem";

type DeleteMenuItemProps = {
	onClick: () => void;
};

const DeleteMenuItem = (props: DeleteMenuItemProps): ReactElement => {
	const {
		onClick
	} = props;

	return (
		<IconButton
			kind="transparent-colored"
			icon="trash-can"
			onClick={onClick}
			trackingId="delete-button"
		/>
	);
};
DeleteMenuItem.displayName = "ElementLayout.DeleteMenuItem";

type ImproveTextMenuItemProps = {
	onClick: () => void;
	ref?: Ref<HTMLButtonElement>;
};

const ImproveTextMenuItem = (props: ImproveTextMenuItemProps): ReactElement => {
	const {
		onClick,
		ref
	} = props;

	const {
		smallAvatarUrl
	} = useAiAssistantBranding();

	const clickHandler = (args: MouseEvent): void => {
		args.preventDefault();
		onClick();
	};

	return (
		<ButtonBase
			ref={ref}
			onClick={clickHandler}
			type="button"
			trackingId="improve-text"
			className={styles.improveTextButton}
		>
			<AiAssistantAvatar
				imageUrl={smallAvatarUrl}
				size="x-small"
				trackingId="avatar"
			/>
			<span>
				{localize("_ImproveText")}
			</span>
			<Icon value="chevron-down" />
		</ButtonBase>
	);
};
ImproveTextMenuItem.displayName = "ElementLayout.ImproveTextMenuItem";

ElementLayout.ActionsMenu = ActionsMenu;
ElementLayout.AddElementMenuItem = AddElementMenuItem;
ElementLayout.MoveUpMenuItem = MoveUpMenuItem;
ElementLayout.MoveDownMenuItem = MoveDownMenuItem;
ElementLayout.EditMenuItem = EditMenuItem;
ElementLayout.DuplicateMenuItem = DuplicateMenuItem;
ElementLayout.DeleteMenuItem = DeleteMenuItem;
ElementLayout.ImproveTextMenuItem = ImproveTextMenuItem;

type ElementLayoutContextData = {
	isFocused: boolean;
};

const ElementLayoutContext = createContext<ElementLayoutContextData>({
	isFocused: false
});